<template>
  <div class="mb-5 mb-5">
    <div class="row ml-1">
      <error
        v-if="error"
        variant="no-border"
        message="Debe seleccionar al menos 1 filtro"
      />
    </div>
    <div
      class="rounded mt-0 mt-md-4"
      :class="readOnly && !copyMode ? 'bg-light-gray pt-2 p-3' : ''"
    >
      <div class="row">
        <div class="col-12 col-lg-4">
          <custom-drop-down
            v-if="showSelect"
            v-model="filterSelected"
            @input="addFilter"
            :clearable="false"
            fieldkey="id"
            fieldname="name"
            :options="filterOptions"
            :disabled="readOnly && !copyMode"
            placeholder="Seleccione Filtro"
          />
        </div>
      </div>
      <div class="row mt-3">
        <location-filters
          v-if="
            !hasStudentLevel &&
              (!readOnly || copyMode || (data.id && data.center))
          "
          v-model="locationFilters"
          :data="data"
          :read-only="readOnly && !copyMode"
        />
      </div>
      <div v-if="!readOnly || copyMode" class="row px-3">
        <div class="col-12 px-0">
          <div class="row">
            <div
              v-if="showRequiredLegend"
              class="col-12 d-flex justify-content-end ml-n4 mb-n4 mt-3"
            >
              <p class="font-weight-normal">* requerido</p>
            </div>
            <div class="col-12 mb-4 px-0">
              <filter-row
                v-for="filter in model"
                :key="filter.id"
                :item="filter"
                :conditionals="conditionals"
                :read-only="readOnly && !copyMode"
                @remove="removeFilter"
                :is-mobile="isMobile"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="row px-3">
        <div class="col-12 px-0">
          <div class="row">
            <div class="col-12">
              <filter-row
                v-for="filter in filtersSaved"
                :key="filter.id"
                :item="filter"
                :conditionals="conditionals"
                :read-only="true"
                :show-addons="false"
                @remove="removeFilter"
                :is-mobile="isMobile"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomDropDown from 'custom-drop-down'
import Error from '@/components/common/Error'
import LocationFilters from './LocationFilters'
import rbDataType from '@/utils/constants/rbDataType.js'
import FilterRow from './FilterRow.vue'

export default {
  name: 'Filters',
  components: {
    CustomDropDown,
    Error,
    FilterRow,
    LocationFilters,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Object,
      default: () => {},
    },
    error: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    copyMode: {
      type: Boolean,
      default: false,
    },
    conditionals: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    filterSelected: null,
    filters: [],
    filtersSaved: [],
    showSelect: true,
    oldTopic: 0,
    locationFilters: {
      centerDisabled: true,
      municipalityDisabled: true,
      schoolDisabled: true,
      locationFilters: [],
      academicYear: null,
      center: null,
      municipality: null,
      school: null,
    },
  }),
  computed: {
    hasStudentLevel() {
      return this.userHasPermissions('level.student')
    },
    filtersSelected() {
      return (dataType) => {
        if (dataType == rbDataType.Date) return 'customDatePicker'
        if (dataType == rbDataType.Text || dataType == rbDataType.Number)
          return 'CustomInput'
        if (dataType == rbDataType.Select) return 'CustomDropDown'

        return 'CustomInput'
      }
    },
    currentProperties() {
      return (filter) => {
        if (filter.dataType == rbDataType.Text)
          return { maxlength: '50', 'v-model': filter.value }
        if (filter.dataType == rbDataType.Number)
          return { maskType: 'Number', 'v-model': filter.value }
        if (filter.dataType == rbDataType.Select)
          return {
            options: filter.source,
            fieldkey: 'id',
            fieldname: 'name',
            placeholder: 'Seleccione',
            'v-model': filter.source?.find((e) => e.id == filter.value),
          }
      }
    },
    isDisabled() {
      return (filter) => {
        if (
          filter.dataType === rbDataType.Text ||
          filter.dataType === rbDataType.Select
        ) {
          return true
        }
        if (filter.dataType === 'Date' && filter.id === 12) {
          return true
        }

        return false
      }
    },
    filterOptions() {
      const items =
        this.data.columns
          .filter((e) => e.filterable)
          .filter((el) => {
            return !this.model.find((rm) => {
              const id = rm.columnId ? rm.columnId : rm.id
              return (
                id === el.id || rm.name.toLowerCase() === el.name.toLowerCase()
              )
            })
          }) || []

      const arrayUnique = [
        ...new Map(items.map((item) => [item['name'], item])).values(),
      ]

      return arrayUnique
    },
    model: {
      get() {
        return this.value || {}
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    showRequiredLegend() {
      return this.model.filter((e) => e.required).length > 0
    },
    setValue() {
      return (filter) => {
        if (
          filter.dataType == rbDataType.Text ||
          filter.dataType == rbDataType.Number
        ) {
          return filter.value ? filter.value : filter.model.value
        }
        if (filter.dataType == rbDataType.Select) {
          const value = filter.value ? filter.value : filter.model.value
          const id = typeof value === 'string' ? value : value.id
          return filter.source?.find((e) => e.id == id)
        }
      }
    },
  },
  watch: {
    'data.topic'(newValue) {
      if (this.oldTopic != newValue) {
        if (
          !this.readOnly &&
          (this.data.id === 0 ||
            this.data.id == undefined ||
            this.data.id == null)
        )
          this.clear()
      }
      this.oldTopic = newValue
    },
    readOnly: {
      immediate: true,
      handler(value) {
        if (value && !this.editMode) {
          this.setReadOnly()
        } else {
          if (!this.copyMode) this.model = this.config(this.model)
        }
      },
    },
    editMode: {
      immediate: true,
      handler(value) {
        if (value) {
          this.model = this.config(this.model)
        }
      },
    },
  },
  methods: {
    addFilter() {
      this.model.push({
        ...this.filterSelected,
        model: {
          conditional: this.conditionals[0],
          value: null,
        },
      })
      this.filterSelected = null
      this.showSelect = false
      setTimeout(() => {
        this.showSelect = true
      }, 10)
    },
    removeFilter(item) {
      const idx = this.model.indexOf(item)
      this.model.splice(idx, 1)
    },
    setReadOnly() {
      this.filtersSaved = this.config(this.model)
    },
    clear() {
      this.filterSelected = null
      this.filters = []
      this.filtersSaved = []
      this.model = []
      this.data.center = null
      this.data.municipality = null
      this.data.school = null
    },
    config(value) {
      const items = value?.map((e) => {
        let data = null
        if (e?.value || e?.model?.value) {
          const value = e.value ? e.value : e.model.value
          if (value) {
            const id = typeof value === 'string' ? value : value.id

            let result = this.getValue(e, id)

            data =
              e.dataType == rbDataType.Select || e.dataType == rbDataType.Date
                ? result
                : value
          }
        }
        return {
          ...e,
          model: {
            conditional: this.conditionals[0],
            value: data,
          },
        }
      })
      return items || []
    },
    getValue(e, id) {
      if (e.dataType == rbDataType.Select) {
        if (e.isMultiSelect) {
          const ids = e.value.split(',').map((e) => parseInt(e))
          return e.source
            ?.filter((m) => ids.includes(m.id))
            .map((e) => ({ ...e, selected: true }))
        } else {
          return e.source?.find((m) => m.id == id)
        }
      }

      if (e.dataType == rbDataType.Date) {
        if (e.isMultiSelect) {
          return [new Date(e.value), new Date(e.value2)]
        } else {
          return e.value
        }
      }
    },
  },
}
</script>
<style scoped lang="scss">
.text-red {
  color: #c9545e;
}

::v-deep .custom-datetime-picker-container.width-container {
  width: 100% !important;
}

.w-80 {
  width: 80%;
}

.w-20 {
  width: 20%;
}

.cursor-pointer {
  cursor: pointer;
}

.filter-row {
  background-color: #fce0c8;
  width: 50%;
  border-radius: 4px;
  padding: 1.5rem;
}

::v-deep .filter-row-saved {
  border-radius: 4px;
  padding: 1rem;
  border: 1px solid #e2e2e2;
}

.btn-circle {
  width: 35px;
  height: 35px;
  text-align: center;
  padding: 5px 0;
  font-size: 12px;
  line-height: 1.428571429;
  border-radius: 20px;
}

@media only screen and (max-width: 600px) {
  .filter-row-saved {
    padding: 0.5rem;
  }

  ::v-deep div.vs__dropdown-toggle {
    padding: 0.5rem 0 !important;
  }
}
::v-deep .custom-ddl {
  width: 100%;
}
</style>
